const STUB_ATTR = 'data-lit-note-img';

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function fileLabelFromSrc(src: string): string {
  try {
    const noQuery = src.split('?')[0] || src;
    const last = decodeURIComponent(noQuery.split('/').pop() || '');
    return last.trim() || '图片';
  } catch {
    return '图片';
  }
}

/**
 * 窄区编辑：将 &lt;img&gt; 转为带标记的外链（仍保存为笔记 HTML 的「逻辑图」，
 * 在放大编辑 / 服务端再还原为 img）。
 */
/** 从 img 标签片段解析宽度（Quill / resize 可能写 width、width="400px" 或 style） */
function widthFromImgInner(inner: string): string {
  const wAttr = /\bwidth=["']?(\d+)px?["']?/i.exec(inner);
  if (wAttr) return wAttr[1];
  const wStyle = /\bstyle=["'][^"']*width\s*:\s*(\d+)px/i.exec(inner);
  if (wStyle) return wStyle[1];
  return '';
}

function decodeHrefEntities(href: string): string {
  return href
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&#x0*27;/gi, "'");
}

/**
 * 侧栏桩链接在 href 上带 `#litw=宽度`，避免 Quill 剥掉 data-* 后无法恢复缩放尺寸。
 * 浏览器加载图片时会忽略 hash，不影响 /api/uploads 等资源。
 */
function parseLitwFromUrl(rawUrl: string): { cleanUrl: string; widthPx?: string } {
  const decoded = decodeHrefEntities(rawUrl.trim());
  const idx = decoded.indexOf('#litw=');
  if (idx === -1) return { cleanUrl: decoded };
  const cleanUrl = decoded.slice(0, idx);
  const rest = decoded.slice(idx + 6);
  const m = /^(\d+)/.exec(rest);
  return { cleanUrl, widthPx: m ? m[1] : undefined };
}

function appendLitwFragment(src: string, w: string): string {
  if (!w) return src;
  const base = src.split('#')[0];
  return `${base}#litw=${w}`;
}

export function notesImagesToStubLinks(html: string): string {
  if (!html || !/<img\b/i.test(html)) return html;
  return html.replace(/<img\b([^>]*)>/gi, (full, inner) => {
    const srcM = /\bsrc=["']([^"']+)["']/i.exec(inner);
    if (!srcM) return full;
    const src = srcM[1];
    const w = widthFromImgInner(inner);
    const hrefForStub = appendLitwFragment(src, w);
    const label = escapeHtml(fileLabelFromSrc(src));
    const parts = [
      '<a',
      `${STUB_ATTR}="1"`,
      `class="lit-note-img-stub"`,
      `href="${escapeAttr(hrefForStub)}"`,
      'target="_blank"',
      'rel="noopener noreferrer"'
    ];
    if (w) parts.push(`data-lit-img-w="${escapeAttr(w)}"`);
    parts.push(`>📎 ${label}</a>`);
    return parts.join(' ');
  });
}

/**
 * 从侧栏「链接桩」还原为 img，供保存与放大编辑器使用。
 * 仅依赖 `data-lit-note-img="1"`：Quill 常会去掉自定义 class，不再要求 lit-note-img-stub。
 */
export function notesStubLinksToImages(html: string): string {
  if (!html || !html.includes(STUB_ATTR)) return html;
  return html.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (full, attrs) => {
    if (!/\bdata-lit-note-img=["']1["']/.test(attrs)) return full;
    const hrefM = /\bhref=["']([^"']+)["']/i.exec(attrs);
    if (!hrefM) return full;
    const { cleanUrl, widthPx } = parseLitwFromUrl(hrefM[1]);
    const wM = /\bdata-lit-img-w=["']([^"']+)["']/i.exec(attrs);
    const w = wM ? wM[1] : widthPx;
    let out = `<img src="${escapeAttr(cleanUrl)}" alt=""`;
    if (w) out += ` width="${escapeAttr(w)}"`;
    out += ' />';
    return out;
  });
}

function isLikelyImageHref(href: string): boolean {
  const t = decodeHrefEntities(href.trim());
  if (!t) return false;
  if (/^data:image\//i.test(t)) return true;
  const path = t.split(/[?#]/)[0].toLowerCase();
  return /\.(png|jpe?g|gif|webp|svg|bmp|ico|avif|heic|heif)$/i.test(path);
}

/**
 * Quill 常把 data-lit-note-img 等自定义属性剥掉，只剩「📎 文件名」+ 图片 URL 的普通链接。
 * 在放大编辑等场景把这类链接还原为 img。
 */
export function notesHeuristicImageLinksToImages(html: string): string {
  if (!html || !/<a\b/i.test(html)) return html;
  return html.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (full, attrs, inner) => {
    const hrefM = /\bhref=["']([^"']+)["']/i.exec(attrs);
    if (!hrefM) return full;
    const hrefRaw = hrefM[1];
    const { cleanUrl, widthPx } = parseLitwFromUrl(hrefRaw);
    if (!isLikelyImageHref(cleanUrl)) return full;
    const textOnly = inner.replace(/<[^>]+>/g, '').replace(/\uFEFF/g, '').trim();
    /** Quill 常剥掉「📎」或改写锚点文字；本地上传图几乎总在 /uploads/ 下 */
    const hasPaperclip = /^[\s\u200B]*📎/.test(textOnly);
    const isOurUpload =
      /\/uploads\//i.test(cleanUrl) || /\/api\/uploads\//i.test(cleanUrl);
    if (!hasPaperclip && !isOurUpload) return full;
    const wM = /\bdata-lit-img-w=["']([^"']+)["']/i.exec(attrs);
    const w = wM ? wM[1] : widthPx;
    let out = `<img src="${escapeAttr(cleanUrl)}" alt=""`;
    if (w) out += ` width="${escapeAttr(w)}"`;
    out += ' />';
    return out;
  });
}

/** 笔记 HTML 统一成以 &lt;img&gt; 存图（先桩属性还原，再启发式处理被 Quill 剥属性的链接） */
export function normalizeNotesHtmlToImgEmbed(html: string): string {
  const pass1 = notesStubLinksToImages(html || '');
  return notesHeuristicImageLinksToImages(pass1);
}
